/**
 * tenantKillSwitchService — authFetch wrappers around
 * /api/tenant/me/kill-switch/.
 *
 * WQ-232 (Phase 4.9 α6). Tenant-scoped self-service surface for
 * per-capability halt. Tenant scope is JWT-derived on the backend; the
 * frontend never sends a tenant_id parameter on these calls.
 *
 *   GET  /api/tenant/me/kill-switch/list-toggles
 *   GET  /api/tenant/me/kill-switch/active
 *   POST /api/tenant/me/kill-switch/halt          { capability_type, reason? }
 *   POST /api/tenant/me/kill-switch/restore       { capability_type, reason? }
 *   POST /api/tenant/me/kill-switch/halt-all      { reason? }
 *   POST /api/tenant/me/kill-switch/restore-all   { reason? }
 *
 * Mutating endpoints require tenant_admin / admin / superadmin role on
 * the JWT — backend returns 403 with TENANT_ADMIN_REQUIRED otherwise.
 */

export interface HaltRecord {
  id: string;
  tenant_id: string;
  capability_type: string;
  halted_at: string;
  halted_by: string;
  halted_by_role: string;
  halt_reason: string | null;
  restored_at: string | null;
  restored_by: string | null;
  restored_by_role: string | null;
  restore_reason: string | null;
  is_active: boolean;
}

export interface HaltToggle {
  capability_type: string;
  is_halted: boolean;
  halt_record: HaltRecord | null;
}

export interface HaltAllResponse {
  halted: HaltRecord[];
  skipped_already_halted: string[];
}

export interface RestoreAllResponse {
  restored: HaltRecord[];
  skipped_not_halted: string[];
}

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/tenant/me/kill-switch';

async function _check(res: Response, label: string): Promise<unknown> {
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      detail = res.statusText;
    }
    throw new Error(`${label} failed: ${res.status} — ${detail}`);
  }
  return res.json();
}

export async function listToggles(authFetch: AuthFetch): Promise<HaltToggle[]> {
  const res = await authFetch(`${BASE}/list-toggles`);
  return (await _check(res, 'listToggles')) as HaltToggle[];
}

export async function listActive(authFetch: AuthFetch): Promise<HaltRecord[]> {
  const res = await authFetch(`${BASE}/active`);
  return (await _check(res, 'listActive')) as HaltRecord[];
}

export async function halt(
  authFetch: AuthFetch,
  capability_type: string,
  reason?: string,
): Promise<HaltRecord> {
  const res = await authFetch(`${BASE}/halt`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ capability_type, reason: reason || null }),
  });
  return (await _check(res, 'halt')) as HaltRecord;
}

export async function restore(
  authFetch: AuthFetch,
  capability_type: string,
  reason?: string,
): Promise<HaltRecord> {
  const res = await authFetch(`${BASE}/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ capability_type, reason: reason || null }),
  });
  return (await _check(res, 'restore')) as HaltRecord;
}

export async function haltAll(
  authFetch: AuthFetch,
  reason?: string,
): Promise<HaltAllResponse> {
  const res = await authFetch(`${BASE}/halt-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || null }),
  });
  return (await _check(res, 'haltAll')) as HaltAllResponse;
}

export async function restoreAll(
  authFetch: AuthFetch,
  reason?: string,
): Promise<RestoreAllResponse> {
  const res = await authFetch(`${BASE}/restore-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || null }),
  });
  return (await _check(res, 'restoreAll')) as RestoreAllResponse;
}
